import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../../lib/cn";
import { useAgentChatStore } from "../../stores/agentChat";
import type { AgentStatus as AgentStatusType } from "../../types/ai";

const STATUS_CONFIG: Record<AgentStatusType, { label: string; color: string } | null> = {
  idle: null,
  thinking: { label: "Thinking", color: "var(--color-lever-deep)" },
  reading: { label: "Reading", color: "var(--color-cool-azure)" },
  compiling: { label: "Compiling", color: "var(--color-signal-green)" },
  editing: { label: "Editing", color: "var(--color-ember-orange)" },
  done: { label: "Done", color: "var(--color-signal-green)" },
};

export function AgentStatusIndicator({ className }: { className?: string }) {
  const agentStatus = useAgentChatStore((s) => s.agentStatus);
  const config = STATUS_CONFIG[agentStatus];
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    setElapsed(0);
    if (agentStatus === "idle" || agentStatus === "done") return;
    const interval = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(interval);
  }, [agentStatus]);

  return (
    <AnimatePresence>
      {config && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9, width: 0 }}
          animate={{ opacity: 1, scale: 1, width: "auto" }}
          exit={{ opacity: 0, scale: 0.9, width: 0 }}
          transition={{ duration: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
          className={cn("flex items-center gap-1.5 overflow-hidden", className)}
        >
          <motion.div
            className="h-1.5 w-1.5 rounded-full shrink-0"
            style={{ backgroundColor: config.color }}
            animate={
              agentStatus !== "done"
                ? { opacity: [1, 0.4, 1], scale: [1, 0.85, 1] }
                : { opacity: 1, scale: 1 }
            }
            transition={
              agentStatus !== "done"
                ? { duration: 1.5, repeat: Infinity, ease: "easeInOut" }
                : { duration: 0.2 }
            }
          />
          <span
            className="text-[11px] font-medium whitespace-nowrap"
            style={{ color: config.color }}
          >
            {config.label}
          </span>
          {elapsed > 1 && agentStatus !== "done" && (
            <span className="text-[9px] tabular-nums whitespace-nowrap text-[var(--text-muted)] opacity-60">
              {elapsed}s
            </span>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
